// Serviço do domínio consents — termos, aceites e assinaturas.
import { createClient } from '@/lib/supabase/client'
import { registrarAuditoria } from '@/services/identity'
import { registrarEvento } from '@/services/timeline'

export type ExigenciaTermo = 'informativo' | 'recomendado' | 'obrigatorio' | 'critico'
export type ComportamentoTermo = 'alertar' | 'aceite' | 'assinatura' | 'bloquear'
export type StatusTermo =
  | 'pendente' | 'enviado' | 'visualizado' | 'aceito' | 'assinado'
  | 'recusado' | 'vencido' | 'revogado' | 'substituido'

export interface TermoModelo {
  id: string
  nome: string
  ativo: boolean
}

export interface TermoVersao {
  id: string
  termo_modelo_id: string
  numero: number
  conteudo: string
  publicada: boolean
}

export interface TermoModeloComVersao extends TermoModelo {
  versao_atual: TermoVersao | null
  rascunho: TermoVersao | null
}

export interface TermoSolicitacao {
  id: string
  paciente_id: string
  consulta_id: string | null
  termo_versao_id: string
  exigencia: ExigenciaTermo
  comportamento: ComportamentoTermo
  status: StatusTermo
  excecao_autorizada: boolean
  excecao_justificativa: string | null
  created_at: string
}

export const EXIGENCIA_LABEL: Record<ExigenciaTermo, string> = {
  informativo: 'Informativo',
  recomendado: 'Recomendado',
  obrigatorio: 'Obrigatório',
  critico: 'Crítico',
}

export const COMPORTAMENTO_LABEL: Record<ComportamentoTermo, string> = {
  alertar: 'Apenas alertar',
  aceite: 'Solicitar aceite',
  assinatura: 'Exigir assinatura',
  bloquear: 'Assinar ou bloquear a consulta',
}

export const STATUS_TERMO_LABEL: Record<StatusTermo, string> = {
  pendente: 'Pendente',
  enviado: 'Enviado',
  visualizado: 'Visualizado',
  aceito: 'Aceito',
  assinado: 'Assinado',
  recusado: 'Recusado',
  vencido: 'Vencido',
  revogado: 'Revogado',
  substituido: 'Substituído',
}

export async function listTermoModelos(workspaceId: string): Promise<TermoModeloComVersao[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('termo_modelos')
    .select('id, nome, ativo, termo_versoes(*)')
    .eq('workspace_id', workspaceId)
    .order('nome')
  if (error) throw error
  return (data ?? []).map(row => {
    const versoes = ((row as Record<string, unknown>).termo_versoes as TermoVersao[] | null) ?? []
    versoes.sort((a, b) => b.numero - a.numero)
    const { termo_versoes: _drop, ...resto } = row as Record<string, unknown>
    void _drop
    return {
      ...(resto as unknown as TermoModelo),
      versao_atual: versoes.find(v => v.publicada) ?? null,
      rascunho: versoes.find(v => !v.publicada) ?? null,
    }
  })
}

export async function criarTermoModelo(workspaceId: string, nome: string): Promise<void> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('termo_modelos')
    .insert({ workspace_id: workspaceId, nome: nome.trim() })
    .select('id')
    .single()
  if (error) throw error
  const { error: e2 } = await supabase.from('termo_versoes').insert({
    workspace_id: workspaceId,
    termo_modelo_id: (data as { id: string }).id,
    numero: 1,
    publicada: false,
  })
  if (e2) throw e2
}

export async function salvarConteudoRascunho(workspaceId: string, versaoId: string, conteudo: string): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('termo_versoes')
    .update({ conteudo })
    .eq('id', versaoId)
    .eq('workspace_id', workspaceId)
  if (error) throw error
}

export async function publicarTermoVersao(workspaceId: string, versaoId: string): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('termo_versoes')
    .update({ publicada: true })
    .eq('id', versaoId)
    .eq('workspace_id', workspaceId)
  if (error) throw error
  await registrarAuditoria({ workspaceId, acao: 'termo_publicado', entidade: 'termo_versoes', registroId: versaoId })
}

export async function novaVersaoTermo(workspaceId: string, modelo: TermoModeloComVersao): Promise<TermoVersao> {
  const supabase = createClient()
  const numero = Math.max(modelo.versao_atual?.numero ?? 0, modelo.rascunho?.numero ?? 0) + 1
  const { data, error } = await supabase
    .from('termo_versoes')
    .insert({
      workspace_id: workspaceId,
      termo_modelo_id: modelo.id,
      numero,
      conteudo: modelo.versao_atual?.conteudo ?? '',
      publicada: false,
    })
    .select('*')
    .single()
  if (error) throw error
  return data as TermoVersao
}

export async function listSolicitacoesDoPaciente(workspaceId: string, pacienteId: string): Promise<TermoSolicitacao[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('termo_solicitacoes')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('paciente_id', pacienteId)
    .order('created_at', { ascending: false })
    .limit(100)
  if (error) throw error
  return (data ?? []) as TermoSolicitacao[]
}

function gerarToken(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

/** Cria a solicitação e o link tokenizado do paciente. Retorna o caminho /p/<token>. */
export async function solicitarTermo(params: {
  workspaceId: string
  pacienteId: string
  consultaId?: string | null
  termoVersaoId: string
  exigencia: ExigenciaTermo
  comportamento: ComportamentoTermo
}): Promise<{ solicitacaoId: string; path: string }> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('termo_solicitacoes')
    .insert({
      workspace_id: params.workspaceId,
      paciente_id: params.pacienteId,
      consulta_id: params.consultaId ?? null,
      termo_versao_id: params.termoVersaoId,
      exigencia: params.exigencia,
      comportamento: params.comportamento,
      status: 'enviado',
    })
    .select('id')
    .single()
  if (error) throw error
  const solicitacaoId = (data as { id: string }).id

  const token = gerarToken()
  const { error: e2 } = await supabase.from('links_paciente').insert({
    workspace_id: params.workspaceId,
    paciente_id: params.pacienteId,
    consulta_id: params.consultaId ?? null,
    termo_solicitacao_id: solicitacaoId,
    escopo: 'termo',
    token,
  })
  if (e2) throw e2

  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.pacienteId,
    consultaId: params.consultaId ?? null,
    tipo: 'termo_enviado',
    titulo: 'Termo enviado para a paciente',
    eventoDominio: 'consent.requested',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'termo_solicitado',
    entidade: 'termo_solicitacoes',
    registroId: solicitacaoId,
  })
  return { solicitacaoId, path: `/p/${token}` }
}

export async function revogarSolicitacao(workspaceId: string, solicitacao: TermoSolicitacao): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('termo_solicitacoes')
    .update({ status: 'revogado' })
    .eq('id', solicitacao.id)
    .eq('workspace_id', workspaceId)
  if (error) throw error
  await registrarAuditoria({
    workspaceId,
    acao: 'termo_revogado',
    entidade: 'termo_solicitacoes',
    registroId: solicitacao.id,
  })
}

/** Exceção autorizada (só médico; o banco valida e carimba autor/justificativa). */
export async function autorizarExcecao(workspaceId: string, solicitacao: TermoSolicitacao, justificativa: string): Promise<void> {
  if (!justificativa.trim()) throw new Error('Exceção exige justificativa.')
  const supabase = createClient()
  const { error } = await supabase
    .from('termo_solicitacoes')
    .update({ excecao_autorizada: true, excecao_justificativa: justificativa.trim() })
    .eq('id', solicitacao.id)
    .eq('workspace_id', workspaceId)
  if (error) throw error
  await registrarAuditoria({
    workspaceId,
    acao: 'termo_excecao_autorizada',
    entidade: 'termo_solicitacoes',
    registroId: solicitacao.id,
    dados: { justificativa: justificativa.trim() },
  })
}
